import React, { useState } from 'react';
import { StyledContainer } from '../../../Content.styles';
import { TableBody, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { StyledTableRow, DetailsTableCell, StyledTable, StyledTitle } from './ReportTable.styles';
import { Dialog } from '@cambridgeassessment/cambridge-ui';
import { BlackImportExportIcon } from '../MalpracticeReportsSummary/MalpracticeReportsSummary';
import { Waypoint } from 'react-waypoint';
import { SortOrder } from '@cambridgeassessment/lib-ee-commons';
import Loader from '../../../../../components/Loader/Loader';

export type SortFunction<T> = {
  sort: (params?: keyof T) => () => void;
  reportSummarySortingParams: SortingParams<T>;
};
type SortCallback<T> = (sortingParams: SortingParams<T>) => void;

export interface SortingParams<T> {
  fieldName: keyof T;
  order: SortOrder;
}

export enum ColumnWidth {
  Wide = 150,
}

interface ReportTableProps<T> {
  data: T[];
  columns: {
    label: string;
    name?: keyof T;
    centre: boolean;
    sortable: boolean;
    renderItem?: (dataItem: T) => React.ReactNode;
    width?: ColumnWidth;
  }[];
  onSort: SortFunction<T>['sort'];
  defaultSorting?: SortingParams<T>;
  loadMoreItems: () => void;
  hasMoreItems?: boolean;
}

export const useSorting = <T extends {}>(
  sortCallback: SortCallback<T>,
  defaultSorting: SortingParams<T>,
): SortFunction<T> => {
  const [reportSummarySortingParams, setReportSummarySortingParams] = useState<SortingParams<T>>(defaultSorting);

  const sort: SortFunction<T>['sort'] = (fieldName) => (): void => {
    if (fieldName) {
      const reverseOrder: Record<SortOrder, SortOrder> = {
        [SortOrder.ASC]: SortOrder.DESC,
        [SortOrder.DESC]: SortOrder.ASC,
      };
      const currentSortField = reportSummarySortingParams.fieldName;
      const currentSortOrder = reportSummarySortingParams.order;

      const sortingParams = {
        fieldName,
        order: currentSortField === fieldName ? reverseOrder[currentSortOrder] : SortOrder.ASC,
      };

      setReportSummarySortingParams(sortingParams);
      sortCallback(sortingParams);
    }
  };

  return { sort, reportSummarySortingParams };
};

export const ReportTable = <T extends {}>({
  columns,
  data,
  loadMoreItems,
  onSort,
  hasMoreItems,
}: ReportTableProps<T>): JSX.Element => {
  if (data.length === 0) {
    return (
      <StyledContainer data-qa-id='page-priming-malpractice-reports'>
        <StyledTitle data-qa-id='search-result-message'>No results found for your search.</StyledTitle>
        <Dialog>Please navigate back to the previous screen and try again.</Dialog>
      </StyledContainer>
    );
  }

  return (
    <>
      <StyledTable data-qa-id='report-details-table'>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => {
              const sortable = col.sortable && data.length > 1;
              return (
                <DetailsTableCell key={index} align={col.centre ? 'center' : 'left'} data-qa-id={col.label + '-title'}>
                  <TableSortLabel
                    active={sortable}
                    disabled={!sortable}
                    hideSortIcon={!sortable}
                    IconComponent={BlackImportExportIcon}
                    onClick={col.name ? onSort(col.name) : onSort()}
                  >
                    {col.label}
                  </TableSortLabel>
                </DetailsTableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, rowIndex) => (
            <StyledTableRow key={rowIndex}>
              {columns.map((col) => {
                const cellContent = col.renderItem ? col.renderItem(row) : col.name && row[col.name];
                return (
                  <DetailsTableCell
                    key={col.label}
                    align={col.centre ? 'center' : 'left'}
                    data-qa-id={`${col.name}-${rowIndex}`}
                    style={{ minWidth: col.width }}
                  >
                    {cellContent}
                  </DetailsTableCell>
                );
              })}
            </StyledTableRow>
          ))}
        </TableBody>
        <Waypoint onEnter={() => loadMoreItems()} />
      </StyledTable>
      {hasMoreItems && <Loader />}
    </>
  );
};

export default ReportTable;
